import { ApolloCache, Reference, StoreObject, useMutation, useQuery } from '@apollo/client'
import {
  Button,
  ButtonGroup,
  Modal,
  StackDivider,
  useBreakpointValue,
  useColorModeValue as mode,
  useDisclosure,
  VStack
} from '@chaine/keychaine'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DELETE_WORKSPACE_ACCESS_REQUEST } from '../../layouts/application/main-container/data/mutations'
import {
  GET_USER_WORKSPACE_ACCESS_REQUEST,
  IWorkspaceAccessRequest
} from '../../layouts/application/main-container/data/queries'
import { IWorkspace } from '../../types'
import { Toast } from '..'
import { WorkspaceAccessRequest } from './workspace-access-request'
import { WorkspaceAccessRequestFlowPopup } from './workspace-access-request-flow-popup'

/**
 * WorkspaceAccessRequests component displays all active workspace access request to the user and provides the option to accept or decline them.
 */
export const WorkspaceAccessRequests = React.memo(() => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isOpenVerificationFlow,
    onOpen: onOpenVerificationFlow,
    onClose: onCloseVerificationFlow
  } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false })
  const workspaceIDRef = useRef<string>('')
  const { showToast } = Toast()

  const [selectedWorkspace, setSelectedWorkspace] = useState<IWorkspace | null>(null)
  const [confirmationWorkspace, setConfirmationWorkspace] = useState<IWorkspace | null>(null)

  /** Fetches active workspace access requests for the current user */
  const { data, refetch: refetchAccessRequests } = useQuery<{ workspaceAccessRequests: IWorkspaceAccessRequest[] }>(
    GET_USER_WORKSPACE_ACCESS_REQUEST
  )

  /** Mutation delete access request */
  const [deleteAccessRequest, { loading: deleting }] = useMutation(DELETE_WORKSPACE_ACCESS_REQUEST, {
    onCompleted: () => {
      setConfirmationWorkspace(null)
    },
    onError: () => {
      showToast({ status: 'error', title: 'An error occurred while deleting the access request.' })
      onClose()
    },
    update(cache) {
      modifyCache(cache)
    }
  })

  /** Handler to accept an access request and open the verification flow */
  const handleAcceptAccessRequest = useCallback(
    (workspace: IWorkspace) => {
      setSelectedWorkspace(workspace)
      if (workspace.id) {
        workspaceIDRef.current = workspace.id
        onOpenVerificationFlow()
      }
    },
    [onOpenVerificationFlow]
  )

  /** Handler to delete an access request */
  const handleDeleteAccessRequest = useCallback((workspaceID: string, workspace: IWorkspace | null) => {
    workspaceIDRef.current = workspaceID
    setConfirmationWorkspace(workspace)
  }, [])

  /** Updates the Apollo cache after an access request is deleted */
  const modifyCache = useCallback((cache: ApolloCache<unknown>) => {
    const workspaceID = workspaceIDRef.current
    cache.modify({
      fields: {
        workspaceAccessRequests(existingInvitations, { readField }) {
          return existingInvitations.filter(
            (existingInvitation: Reference | StoreObject | undefined) =>
              workspaceID !== readField('workspaceID', existingInvitation)
          )
        }
      }
    })
  }, [])

  /** Closes the verification flow */
  const onHandleClose = useCallback(() => {
    setSelectedWorkspace(null)
    onCloseVerificationFlow()
  }, [onCloseVerificationFlow, setSelectedWorkspace])

  /** Handler to delete an access request and close the verification flow */
  const onHandleFinish = useCallback(() => {
    handleDeleteAccessRequest(workspaceIDRef.current, confirmationWorkspace)
    onHandleClose()
  }, [handleDeleteAccessRequest, onHandleClose, confirmationWorkspace])

  /** Open or close the modal based on the presence of access requests */
  useEffect(() => {
    if (!data) return
    if (data.workspaceAccessRequests?.length > 0) {
      onOpen()
    } else if (data.workspaceAccessRequests?.length === 0) {
      // Close the modal if no access requests remain
      onClose()
      // Also close the verification flow if it's open
      if (isOpenVerificationFlow) {
        onHandleClose()
      }
    }
  }, [data, onOpen, onClose, isOpenVerificationFlow, onHandleClose])

  return (
    <>
      {isOpenVerificationFlow && selectedWorkspace?.dot && (
        <WorkspaceAccessRequestFlowPopup
          isOpen={isOpenVerificationFlow}
          onClose={onHandleClose}
          workspaceID={workspaceIDRef.current}
          dot={selectedWorkspace.dot}
          onHandleFinish={onHandleFinish}
          onVerificationSuccess={refetchAccessRequests}
        />
      )}
      <Modal
        size={isMobile ? 'full' : '2xl'}
        isCentered={!isMobile}
        motionPreset={isMobile ? 'slideInBottom' : 'scale'}
        isOpen={isOpen}
        onClose={() => {
          setConfirmationWorkspace(null)
          onClose()
        }}
        title={confirmationWorkspace ? 'Are you sure?' : 'Do you belong to this company(s)?'}
        subtitle={
          confirmationWorkspace
            ? `Doing this will prevent you from receiving load opportunities that are sent to ${confirmationWorkspace.displayName}, DOT#${confirmationWorkspace.dot}.`
            : "To keep Chain's network secure, all users must complete a quick verification to confirm they're authorized to act on behalf of this company. If you have already verified in the past, we have enhanced our verification logic and request previously verified users to also re-verify. We appreciate your patience and apologize for any inconvenience. At Chain, we take security of our ecosystem seriously. This step will create a more safe environment for carriers and brokers."
        }
      >
        {confirmationWorkspace ? (
          <ButtonGroup display="flex" justifyContent="space-between" w="100%" py={4}>
            <Button variant="primary" w="40%" onClick={() => setConfirmationWorkspace(null)}>
              Back
            </Button>
            <Button
              w="40%"
              variant={'solid'}
              color={mode(undefined, 'gray.100')}
              onClick={() => {
                onHandleClose()
                deleteAccessRequest({ variables: { workspaceID: workspaceIDRef.current } })
              }}
              isLoading={deleting}
            >
              Yes
            </Button>
          </ButtonGroup>
        ) : (
          <VStack
            divider={<StackDivider borderColor={mode('gray.200', 'gray.700')} />}
            spacing={4}
            align="stretch"
            maxHeight="25rem"
            overflow="scroll"
            px={6}
            py={4}
          >
            {data?.workspaceAccessRequests?.map(({ workspace }: IWorkspaceAccessRequest) => {
              if (workspace.id) {
                return (
                  <WorkspaceAccessRequest
                    key={workspace.id}
                    workspace={workspace}
                    onAccept={handleAcceptAccessRequest}
                    onDecline={(id) => handleDeleteAccessRequest(id, workspace)}
                    deleted={deleting}
                    workspaceIDRef={workspaceIDRef}
                  />
                )
              }
            })}
          </VStack>
        )}
      </Modal>
    </>
  )
})

WorkspaceAccessRequests.displayName = 'WorkspaceAccessRequests'
